import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import LegacyButton from '../LegacyButton';
import Typography from '../Typography';
import InputGroup from '../InputGroup';
import { Icons } from '@ohif/ui-next';

const StudyListFilter = ({
  filtersMeta,
  allFiltersMeta,
  filterValues,
  onChange,
  clearFilters,
  isFiltering,
  numOfStudies,
  onUploadClick,
  getDataSourceConfigurationComponent,
}) => {
  const { t } = useTranslation('StudyList');
  const { sortBy, sortDirection } = filterValues;
  const filterSorting = { sortBy, sortDirection };
  const setFilterSorting = sortingValues => {
    onChange({
      ...filterValues,
      ...sortingValues,
    });
  };
  const isSortingEnabled = true;
  const [rolUser, setRolUser] = useState(''); // Added setRolUser state
  useEffect(() => {
    const localStr = localStorage.getItem('usuario');
    if (localStr) {
      try {
        const session = JSON.parse(localStr);
        const tiposUsuario: string = session?.user?.role || '';
        setRolUser(tiposUsuario);
      } catch (error) {
        console.error('Error al parsear el localStorage:', error);
      }
    }
  }, []);
  const activeFilters = [];
  const metaToUse = allFiltersMeta || filtersMeta;
  metaToUse.forEach(meta => {
    let value = filterValues[meta.name];

    if (meta.inputType === 'DateRange' && typeof window !== 'undefined') {
      const isEmptyDateRange =
        !value || (typeof value === 'object' && !value.startDate && !value.endDate);
      if (isEmptyDateRange) {
        const searchParams = new URLSearchParams(window.location.search);
        const urlStartDate = searchParams.get('startDate');
        const urlEndDate = searchParams.get('endDate');
        if (urlStartDate || urlEndDate) {
          value = { startDate: urlStartDate || '', endDate: urlEndDate || '' };
        }
      }
    }

    if (value && value !== '' && meta.name !== 'sortBy' && meta.name !== 'sortDirection') {
      const formatDate = dateStr => {
        if (!dateStr) {
          return '';
        }
        const str = String(dateStr).replace(/[^0-9]/g, '');
        if (str.length === 8) {
          return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
        }
        return dateStr;
      };

      if (typeof value === 'object') {
        if (value.startDate && value.endDate) {
          value = `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
        } else if (value.startDate) {
          value = `${formatDate(value.startDate)} - `;
        } else if (value.endDate) {
          value = ` - ${formatDate(value.endDate)}`;
        } else {
          value = '';
        }
      }

      if (value && String(value).trim() !== '' && String(value).trim() !== '-') {
        activeFilters.push({ label: meta.displayName, value: String(value) });
      }
    }
  });

  return (
    <React.Fragment>
      <div>
        <div className="bg-black">
          <div className="container relative mx-auto flex flex-col pt-5">
            <div className="mb-5 flex flex-row justify-between">
              <div className="flex min-w-[1px] shrink flex-row items-center gap-6">
                <div>
                  <Typography
                    variant="h6"
                    className="text-white"
                  >
                    {t('StudyList')}
                  </Typography>
                  <div className="flex flex-row md:hidden">
                    <Typography
                      variant="h6"
                      className="mr-2 text-sm"
                      data-cy={'num-studies'}
                    >
                      {numOfStudies > 2000 ? '>2000' : numOfStudies}
                    </Typography>
                    <Typography
                      variant="h6"
                      className="text-primary-light text-sm"
                    >
                      {`${t('Studies')} `}
                    </Typography>
                  </div>
                </div>
                {getDataSourceConfigurationComponent && getDataSourceConfigurationComponent()}
                {onUploadClick && (
                  <div
                    className="text-primary-main flex cursor-pointer items-center gap-2 self-center text-lg font-semibold hover:text-primary-light transition-colors"
                    onClick={onUploadClick}
                  >
                    <Icons.Upload />
                    <span>{'Subir'}</span>
                  </div>
                )}
              </div>
              <div className="flex h-[34px] flex-row items-center">
                {/* TODO revisit the completely rounded style of button used for clearing the study list filter - for now use LegacyButton*/}
                {(isFiltering || activeFilters.length > 0) && (
                  <LegacyButton
                    rounded="full"
                    variant="outlined"
                    color="primaryActive"
                    border="primaryActive"
                    className={`md:text-md mx-8 text-sm`}
                    startIcon={<Icons.Cancel />}
                    onClick={clearFilters}
                  >
                    {t('ClearFilters')}
                  </LegacyButton>
                )}
                <div className="flex flex-row max-sm:hidden">
                  <Typography
                    variant="h6"
                    className="mr-2"
                    data-cy={'num-studies'}
                  >
                    {numOfStudies > 2000 ? '>2000' : numOfStudies}
                  </Typography>
                  <Typography
                    variant="h6"
                    className="text-primary-light"
                  >
                    {`${t('Studies')} `}
                  </Typography>
                </div>
              </div>
            </div>
            {/* {activeFilters.length > 0 && (
              <div className="mb-4 flex flex-row flex-wrap items-center gap-2">
                {activeFilters.map(filter => (
                  <div
                    key={filter.label}
                    className="bg-primary-main border-primary-light flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm text-white shadow-sm"
                  >
                    <span className="text-primary-light font-bold">{t(filter.label)}:</span>
                    <span className="max-w-[250px] truncate">{filter.value}</span>
                  </div>
                ))}
              </div>
            )} */}
          </div>
        </div>
      </div>
      <div className="auto container relative sticky -top-1 z-10 m-auto border-b-4 border-black">
        <div className="bg-primary-dark mb-3 rounded pt-3 pb-3">
          <InputGroup
            inputMeta={filtersMeta}
            values={filterValues}
            onValuesChange={onChange}
            sorting={filterSorting}
            onSortingChange={setFilterSorting}
            isSortingEnabled={isSortingEnabled}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

StudyListFilter.propTypes = {
  filtersMeta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      gridCol: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).isRequired,
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  allFiltersMeta: PropTypes.array,
  filterValues: PropTypes.object.isRequired,
  numOfStudies: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  isFiltering: PropTypes.bool.isRequired,
  onUploadClick: PropTypes.func,
  getDataSourceConfigurationComponent: PropTypes.func,
};

export default StudyListFilter;
